import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { OrganizationStore } from '../../../services/stores/organization.store';
import { AlertService } from '../../../services/alert.service';
import {
  ORG_ALREADY_EXISTS_PATTERN,
  ORG_ENTITY_KEY,
  ORG_ENTITY_LABEL,
  ORG_IMPORT_DUPLICATE_MESSAGE,
  ORG_IMPORT_FAILED_CHECK_FILE_MESSAGE,
  ORG_IMPORT_FAILED_MESSAGE,
  ORG_IMPORT_MISSING_ID_MESSAGE,
  ORG_IMPORT_NO_ORG_DATA_MESSAGE,
  ORG_IMPORT_NOT_JSON_MESSAGE,
  ORG_IMPORT_PARSE_FAILED_MESSAGE,
  ORG_IMPORT_READ_FAILED_MESSAGE,
  OrgEntityKey,
} from '../../../models/organization-export-entities';
import { OrgExportPackage, OrgImportEntityResult, OrgImportResult } from '../../../models/organization-export.model';

type ImportMode = 'select' | 'preview' | 'importing' | 'success' | 'error';

interface EntitySummaryRow {
  label: string;
  imported: number;
  skipped: number;
  failed: number;
}

@Component({
  selector: 'app-org-import-dialog',
  templateUrl: './org-import-dialog.component.html',
  styleUrl: './org-import-dialog.component.scss',
  standalone: false,
})
export class OrgImportDialogComponent extends BaseComponent {
  mode: ImportMode = 'select';
  errorMessage = '';
  parsedPackage: Partial<OrgExportPackage> | null = null;
  importResult: OrgImportResult | null = null;
  entitySummaryRows: EntitySummaryRow[] = [];
  // Set when the server rejects the import because an org with this id already exists. Kept
  // separate from errorMessage/'error' mode: retrying is pointless without a different file, so
  // this stays on the Preview screen with Import disabled rather than sending the user through
  // 'Try Again' back to file selection.
  duplicateOrgError = '';

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<OrgImportDialogComponent>,
    private orgStore: OrganizationStore,
    private alertService: AlertService,
  ) {
    super();
  }

  get orgName(): string {
    return this.parsedPackage?.organization?.name ?? '';
  }

  get orgId(): string {
    return this.parsedPackage?.organization?.id ?? '';
  }

  get exportedAt(): string {
    return this.parsedPackage?.exportedAt ?? '';
  }

  get exportedBy(): string {
    return this.parsedPackage?.exportedBy ?? '';
  }

  get userCount(): number {
    return this.parsedPackage?.users?.length ?? 0;
  }

  get groupCount(): number {
    return this.parsedPackage?.groups?.length ?? 0;
  }

  get roleCount(): number {
    return this.parsedPackage?.roles?.length ?? 0;
  }

  get resourcePermissionCount(): number {
    return this.parsedPackage?.resourcePermissions?.length ?? 0;
  }

  get directoryCount(): number {
    return this.parsedPackage?.directories?.length ?? 0;
  }

  get projectCount(): number {
    return this.parsedPackage?.projects?.length ?? 0;
  }

  get workspaceCount(): number {
    return this.parsedPackage?.workspaces?.length ?? 0;
  }

  get nodeCount(): number {
    return this.parsedPackage?.nodes?.length ?? 0;
  }

  get edgeCount(): number {
    return this.parsedPackage?.edges?.length ?? 0;
  }

  get blueprintCount(): number {
    return this.parsedPackage?.blueprints?.length ?? 0;
  }

  get commentCount(): number {
    return this.parsedPackage?.comments?.length ?? 0;
  }

  get localizedNodeCount(): number {
    return this.parsedPackage?.localizedNodes?.length ?? 0;
  }

  get tableSpaceCount(): number {
    return this.parsedPackage?.tableSpaces?.length ?? 0;
  }

  get wikiPageCount(): number {
    return this.parsedPackage?.wikiPages?.length ?? 0;
  }

  get wikiPageVersionCount(): number {
    return this.parsedPackage?.wikiPageVersions?.length ?? 0;
  }

  get wikiYjsUpdateCount(): number {
    return this.parsedPackage?.wikiYjsUpdates?.length ?? 0;
  }

  get auditHistoryCount(): number {
    return this.parsedPackage?.auditHistory?.length ?? 0;
  }

  // Alphabetical, like the export dialog's list, so the same collection is in the same place on both.
  get collectionRows(): { label: string; count: number }[] {
    const pkg = this.parsedPackage;
    const counts: Record<OrgEntityKey, number> = {
      [ORG_ENTITY_KEY.ApiKeys]: pkg?.apiKeys?.length ?? 0,
      [ORG_ENTITY_KEY.AuditHistory]: this.auditHistoryCount,
      [ORG_ENTITY_KEY.Blueprints]: this.blueprintCount,
      [ORG_ENTITY_KEY.Comments]: this.commentCount,
      [ORG_ENTITY_KEY.Directories]: this.directoryCount,
      [ORG_ENTITY_KEY.Edges]: this.edgeCount,
      [ORG_ENTITY_KEY.ExternalLinks]: pkg?.externalLinks?.length ?? 0,
      [ORG_ENTITY_KEY.Groups]: this.groupCount,
      [ORG_ENTITY_KEY.License]: pkg?.license ? 1 : 0,
      [ORG_ENTITY_KEY.LocalizedNodes]: this.localizedNodeCount,
      [ORG_ENTITY_KEY.Nodes]: this.nodeCount,
      [ORG_ENTITY_KEY.Projects]: this.projectCount,
      [ORG_ENTITY_KEY.ResourcePermissions]: this.resourcePermissionCount,
      [ORG_ENTITY_KEY.Roles]: this.roleCount,
      [ORG_ENTITY_KEY.TableSpaces]: this.tableSpaceCount,
      [ORG_ENTITY_KEY.Users]: this.userCount,
      [ORG_ENTITY_KEY.WikiPageVersions]: this.wikiPageVersionCount,
      [ORG_ENTITY_KEY.WikiPages]: this.wikiPageCount,
      [ORG_ENTITY_KEY.WikiYjsUpdates]: this.wikiYjsUpdateCount,
      [ORG_ENTITY_KEY.Workspaces]: this.workspaceCount,
    };
    return Object.values(ORG_ENTITY_KEY)
      .map((key) => ({ label: ORG_ENTITY_LABEL[key], count: counts[key] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get exportVersion(): string {
    return this.parsedPackage?.exportVersion ?? '';
  }

  browseFile() {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (!file.name.toLowerCase().endsWith('.json')) {
      this.alertService.showErrorMessage(ORG_IMPORT_NOT_JSON_MESSAGE);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string) as Partial<OrgExportPackage>;
        this.parsedPackage = parsed;
        this.duplicateOrgError = '';
        if (!this.parsedPackage?.organization) {
          this.errorMessage = ORG_IMPORT_NO_ORG_DATA_MESSAGE;
          this.mode = 'error';
          return;
        }
        if (!this.parsedPackage.organization.id) {
          this.errorMessage = ORG_IMPORT_MISSING_ID_MESSAGE;
          this.mode = 'error';
          return;
        }
        this.mode = 'preview';
        this.checkForDuplicateOrg();
      } catch {
        this.errorMessage = ORG_IMPORT_PARSE_FAILED_MESSAGE;
        this.mode = 'error';
      }
    };
    reader.onerror = () => {
      this.errorMessage = ORG_IMPORT_READ_FAILED_MESSAGE;
      this.mode = 'error';
    };
    reader.readAsText(file);
  }

  get hasFailed(): boolean {
    return (this.importResult?.totalFailed ?? 0) > 0;
  }

  /**
   * Org list is already loaded for the page behind this dialog (org-list.component), so this reads
   * the store's cached snapshot rather than making a request - the duplicate warning should be on
   * screen the moment Preview renders, not only after the user clicks Import and eats a round trip.
   * The 409 handling in confirmImport stays as a fallback for staleness (org list not loaded yet, or
   * another admin importing the same org between preview and click).
   */
  private checkForDuplicateOrg(): void {
    const importedOrgId = this.parsedPackage?.organization?.id;
    if (!importedOrgId) return;
    this.orgStore.$organizations.pipe(take(1)).subscribe((orgs) => {
      const isDuplicate = !!orgs?.some((org) => org?.id === importedOrgId);
      if (isDuplicate) {
        this.duplicateOrgError = ORG_IMPORT_DUPLICATE_MESSAGE;
      }
    });
  }

  confirmImport() {
    if (!this.parsedPackage || this.duplicateOrgError) {
      return;
    }
    this.mode = 'importing';
    this.orgStore.$importOrganization(this.parsedPackage).subscribe({
      next: (res: OrgImportResult) => {
        this.importResult = res;
        this.entitySummaryRows = Object.entries(res?.entities ?? {})
          .map(([key, val]: [string, OrgImportEntityResult]) => ({
            label: ORG_ENTITY_LABEL[key as OrgEntityKey] ?? key,
            imported: val?.imported ?? 0,
            skipped: val?.skipped ?? 0,
            failed: val?.failed ?? 0,
          }))
          .filter((r) => r.imported + r.skipped + r.failed > 0)
          .sort((a, b) => a.label.localeCompare(b.label));
        this.mode = 'success';
      },
      error: (err: any) => {
        const message = err?.error?.detail ?? err?.error?.title ?? err?.error ?? err?.message ?? ORG_IMPORT_FAILED_MESSAGE;
        const messageText = typeof message === 'string' ? message : ORG_IMPORT_FAILED_CHECK_FILE_MESSAGE;

        // The server returns 409 with an "already exists" message when this org id was already
        // imported. Retrying with the same file can only fail the same way, so this stays on the
        // Preview screen with Import disabled instead of routing through the generic error state.
        if (ORG_ALREADY_EXISTS_PATTERN.test(messageText)) {
          this.duplicateOrgError = ORG_IMPORT_DUPLICATE_MESSAGE;
          this.mode = 'preview';
          return;
        }

        this.errorMessage = messageText;
        this.mode = 'error';
      },
    });
  }

  resetToSelect() {
    this.parsedPackage = null;
    this.errorMessage = '';
    this.duplicateOrgError = '';
    this.mode = 'select';
  }

  close() {
    this.dialogRef.close(this.mode === 'success');
  }
}
